/**
 * KeyboardShortcutService
 * 
 * Centralized keyboard shortcut management for the application.
 * Handles all local keyboard shortcuts and dispatches appropriate events.
 */

export interface ShortcutDefinition {
  key: string | string[]  // Key or array of keys (e.g., 'KeyG' or ['Enter', 'Return'])
  code?: string | string[] // Optional code(s) to check
  shift?: boolean
  ctrl?: boolean
  meta?: boolean
  alt?: boolean
  description: string
  action: string | (() => void)  // Event name to dispatch or callback function
  preventDefault?: boolean
  stopPropagation?: boolean
  whenSettingsOpen?: boolean  // Should this work when settings is open?
}

export class KeyboardShortcutService {
  private shortcuts: Map<string, ShortcutDefinition> = new Map()
  private isSettingsOpen = false
  private platform: 'mac' | 'windows' | 'linux'
  private customShortcuts: Map<string, string[]> = new Map() // Store custom shortcuts from settings
  private lastTabTime = 0
  private tabCount = 0
  private tabTimer: number | null = null
  
  constructor() {
    this.platform = this.detectPlatform()
    this.initializeShortcuts()
    this.loadCustomShortcuts()
  }
  
  private detectPlatform(): 'mac' | 'windows' | 'linux' {
    const platform = navigator.platform.toUpperCase()
    if (platform.includes('MAC')) return 'mac'
    if (platform.includes('WIN')) return 'windows'
    return 'linux'
  }
  
  private initializeShortcuts() {
    // Define default shortcuts with IDs that match ShortcutsSettings
    const defaultShortcuts: Array<[string, ShortcutDefinition]> = [
      // Global shortcuts (work everywhere)
      ['unfocus-editor', {
        key: 'Escape',
        description: 'Close settings or unfocus editor',
        action: 'shortcut:escape',
        whenSettingsOpen: true
      }],
      ['settings', {
        key: ',',
        description: 'Toggle settings',
        action: 'shortcut:toggle-settings',
        meta: true,
        ctrl: true,
        shift: false,
        preventDefault: true,
        whenSettingsOpen: true
      }],
      
      // Editor shortcuts (only when settings closed)
      ['check', {
        key: 'G',
        code: 'KeyG',
        meta: true,
        shift: true,
        description: 'Unified check (Grammar & AI)',
        action: 'shortcut:unified-check',
        preventDefault: true,
        stopPropagation: true
      }],
      ['generate', {
        key: 'K',
        code: 'KeyK',
        meta: true,
        shift: false,
        description: 'Generate text with prompt',
        action: 'shortcut:generate',
        preventDefault: true,
        stopPropagation: true
      }],
      ['clear', {
        key: 'L',
        code: 'KeyL',
        meta: true,
        shift: true,
        description: 'Clear text',
        action: 'shortcut:clear-text',
        preventDefault: true,
        stopPropagation: true
      }],
      ['theme', {
        key: 'T',
        code: 'KeyT',
        meta: true,
        shift: true,
        description: 'Toggle theme',
        action: 'shortcut:toggle-theme',
        preventDefault: true,
        stopPropagation: true
      }],
      // Note: sys-copy removed - using native browser copy/paste/cut behavior
      ['apply-all', {
        key: 'Enter',
        meta: true,
        shift: true,
        description: 'Apply all corrections',
        action: 'shortcut:apply-all',
        preventDefault: true,
        stopPropagation: true
      }],
      // ['focus-editor', {
      //   key: 'E',
      //   code: 'KeyE',
      //   meta: true,
      //   shift: true,
      //   description: 'Focus editor',
      //   action: 'shortcut:focus-editor',
      //   preventDefault: true,
      //   stopPropagation: true
      // }], // Conflicts with AI expand (Cmd+Shift+E)
      ['focus-explorer', {
        key: 'E',
        code: 'KeyE',
        meta: true,
        shift: false,
        description: 'Focus file explorer',
        action: 'shortcut:focus-explorer',
        preventDefault: true,
        stopPropagation: true
      }],
      
      // AI features shortcuts
      ['paraphrase', {
        key: 'P',
        code: 'KeyP',
        meta: true,
        shift: true,
        description: '개선 및 변경하기 (Paraphrase)',
        action: 'shortcut:paraphrase',
        preventDefault: true,
        stopPropagation: true
      }],
      ['expand', {
        key: 'E',
        code: 'KeyE',
        meta: true,
        shift: true,
        description: '내용 늘리기 (Expand)',
        action: 'shortcut:expand',
        preventDefault: true,
        stopPropagation: true
      }],
      ['shrink', {
        key: 'S',
        code: 'KeyS',
        meta: true,
        shift: true,
        description: '내용 줄이기 (Shrink)',
        action: 'shortcut:shrink',
        preventDefault: true,
        stopPropagation: true
      }],
      ['summarize', {
        key: 'M',
        code: 'KeyM',
        meta: true,
        shift: true,
        description: '요약하기 (suMmarize)',
        action: 'shortcut:summarize',
        preventDefault: true,
        stopPropagation: true
      }],
      ['continue', {
        key: 'C',
        code: 'KeyC',
        meta: true,
        shift: true,
        description: '이어쓰기 (Continue)',
        action: 'shortcut:continue',
        preventDefault: true,
        stopPropagation: true
      }],
      ['critic', {
        key: 'K',
        code: 'KeyK',
        meta: true,
        shift: true,
        description: '평가받기 (critiK)',
        action: 'shortcut:critic',
        preventDefault: true,
        stopPropagation: true
      }],
      
      // Continuation shortcuts
      ['continue-sentence', {
        key: 'Enter',
        meta: true,
        shift: false,
        description: '한 문장 이어쓰기',
        action: 'shortcut:continue-sentence',
        preventDefault: true,
        stopPropagation: true
      }],
      ['continue-paragraph', {
        key: 'Enter',
        meta: true,
        shift: false,
        description: '한 문단 이어쓰기 (더블 엔터)',
        action: 'shortcut:continue-paragraph',
        preventDefault: true,
        stopPropagation: true
      }]
      
      // Font size shortcuts are handled by early interception in handleKeydown()
      // to prevent browser zoom conflicts. They dispatch these events:
      // - shortcut:increase-font-size (Cmd/Ctrl + Plus/=)
      // - shortcut:decrease-font-size (Cmd/Ctrl + Minus/_)  
      // - shortcut:reset-font-size (Cmd/Ctrl + 0)
    ]
    
    // Register default shortcuts
    defaultShortcuts.forEach(([id, shortcut]) => {
      this.shortcuts.set(id, shortcut)
    })
  }
  
  private loadCustomShortcuts() {
    const savedShortcuts = localStorage.getItem('korean-grammar:shortcuts')
    if (savedShortcuts) {
      try {
        const parsed = JSON.parse(savedShortcuts)
        // Store custom shortcuts for reference
        parsed.forEach((shortcut: any) => {
          if (shortcut.id && shortcut.keys) {
            this.customShortcuts.set(shortcut.id, shortcut.keys)
            // Update the shortcut definition if it exists
            this.updateShortcutFromKeys(shortcut.id, shortcut.keys)
          }
        })
      } catch (error) {
        console.error('Failed to load custom shortcuts:', error)
      }
    }
    
    // Listen for shortcut updates
    window.addEventListener('shortcutsUpdated', (e) => {
      this.handleShortcutsUpdated((e as CustomEvent).detail)
    })
  }
  
  private handleShortcutsUpdated(shortcuts: any[]) {
    shortcuts.forEach((shortcut) => {
      if (shortcut.id && shortcut.keys) {
        this.customShortcuts.set(shortcut.id, shortcut.keys)
        this.updateShortcutFromKeys(shortcut.id, shortcut.keys)
      }
    })
  }
  
  private updateShortcutFromKeys(id: string, keys: string[]) {
    const existingShortcut = this.shortcuts.get(id)
    if (!existingShortcut) return
    
    // Parse keys array into shortcut definition
    const updatedShortcut = { ...existingShortcut }
    
    // Reset modifiers
    updatedShortcut.meta = false
    updatedShortcut.ctrl = false
    updatedShortcut.shift = false
    updatedShortcut.alt = false
    
    // Process each key
    let mainKey = ''
    keys.forEach(key => {
      if (key === 'Cmd') {
        updatedShortcut.meta = true
      } else if (key === 'Ctrl') {
        updatedShortcut.ctrl = true
      } else if (key === 'Shift') {
        updatedShortcut.shift = true
      } else if (key === 'Option' || key === 'Alt') {
        updatedShortcut.alt = true
      } else {
        mainKey = key
      }
    })
    
    // Update the main key
    if (mainKey) {
      updatedShortcut.key = mainKey
      // Update code if it's a letter key
      if (mainKey.length === 1 && /[A-Za-z]/.test(mainKey)) {
        updatedShortcut.code = `Key${mainKey.toUpperCase()}`
      } else if (mainKey === 'Enter' || mainKey === 'Escape' || mainKey === 'Space') {
        updatedShortcut.code = mainKey
      } else {
        delete updatedShortcut.code
      }
    }
    
    // Update the shortcut
    this.shortcuts.set(id, updatedShortcut)
  }
  
  setSettingsOpen(isOpen: boolean) {
    this.isSettingsOpen = isOpen
  }
  
  handleKeydown(e: KeyboardEvent): boolean {
    // Get modifier key based on platform
    const modifierKey = this.platform === 'mac' ? e.metaKey : e.ctrlKey

    // Early interception of font size shortcuts to prevent browser zoom
    if (modifierKey && !e.shiftKey && !e.altKey) {
      if (e.key === '+' || e.key === '=') {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('shortcut:increase-font-size'))
        return true
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('shortcut:decrease-font-size'))
        return true
      } else if (e.key === '0') {
        e.preventDefault()
        window.dispatchEvent(new CustomEvent('shortcut:reset-font-size'))
        return true
      }
    }
    

    // Skip handling ONLY copy/paste/cut shortcuts when in contenteditable
    const activeElement = document.activeElement
    if (activeElement && activeElement.getAttribute('contenteditable') === 'true') {
      // Only skip copy/paste/cut - let browser handle these naturally
      const isCopyPasteCut = modifierKey && !e.shiftKey && ['c', 'v', 'x'].includes(e.key.toLowerCase())
      if (isCopyPasteCut) {
        return false
      }
      // Allow other shortcuts (like AI shortcuts) to work in contenteditable
    }
    
    // Skip handling shortcuts when GenerationBar input is focused
    // The GenerationBar should handle its own shortcuts
    if (activeElement && activeElement.classList.contains('prompt-input')) {
      // Allow standard text editing shortcuts
      if (modifierKey && ['c', 'v', 'x', 'a', 'z', 'y'].includes(e.key.toLowerCase())) {
        // Don't handle these shortcuts - let browser handle them naturally
        return false
      }
      // Allow Cmd+K to close the GenerationBar
      if (modifierKey && e.key.toLowerCase() === 'k') {
        // Don't prevent default, let the shortcut system handle it
        // But don't process other shortcuts
        const shortcut = this.shortcuts.get('generate')
        if (shortcut && typeof shortcut.action === 'string') {
          window.dispatchEvent(new CustomEvent(shortcut.action))
          e.preventDefault()
          return true
        }
      }
      // Allow Escape key to close GenerationBar
      if (e.key === 'Escape') {
        return false // Let GenerationBar handle it
      }
      // Block other global shortcuts when GenerationBar is focused
      if (modifierKey || e.shiftKey) {
        // Don't process other shortcuts but let the key event continue
        return false
      }
    }
    
    // Special handling for Cmd+W (close tab)
    if (e.key === 'w' && modifierKey && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      window.dispatchEvent(new CustomEvent('shortcut:close-tab'))
      return true
    }
    
    // Special handling for Cmd+Enter (continuation)
    if (e.key === 'Enter' && modifierKey && !e.shiftKey) {
      e.preventDefault()
      e.stopPropagation()
      
      const now = Date.now()
      const timeSinceLastEnter = now - this.lastTabTime // Reusing lastTabTime for Enter tracking
      
      // Clear existing timer
      if (this.tabTimer) {
        clearTimeout(this.tabTimer)
        this.tabTimer = null
      }
      
      // If it's been less than 300ms since last enter, it's a double tap
      if (timeSinceLastEnter < 300) {
        this.tabCount++
        if (this.tabCount === 2) {
          // Double enter - continue paragraph
          window.dispatchEvent(new CustomEvent('shortcut:continue-paragraph'))
          this.tabCount = 0
          this.lastTabTime = 0
          return true
        }
      } else {
        // First enter
        this.tabCount = 1
        this.lastTabTime = now
        
        // Set timer to execute single enter action if no second enter comes
        this.tabTimer = window.setTimeout(() => {
          if (this.tabCount === 1) {
            // Single enter - continue sentence
            window.dispatchEvent(new CustomEvent('shortcut:continue-sentence'))
          }
          this.tabCount = 0
          this.lastTabTime = 0
          this.tabTimer = null
        }, 300)
      }
      
      return true
    }
    
    // Find matching shortcut
    for (const [id, shortcut] of this.shortcuts) {
      // Check if shortcut should work when settings is open
      if (this.isSettingsOpen && !shortcut.whenSettingsOpen) {
        continue
      }

      // Check key match (case-insensitive)
      const keys = Array.isArray(shortcut.key) ? shortcut.key : [shortcut.key]
      const keyMatch = keys.some(k => k.toLowerCase() === e.key.toLowerCase())

      // Check code match if specified (case-sensitive for codes)
      let codeMatch = true
      if (shortcut.code) {
        const codes = Array.isArray(shortcut.code) ? shortcut.code : [shortcut.code]
        codeMatch = codes.includes(e.code)
      }

      if (!keyMatch || !codeMatch) {
        continue
      }


      // Check modifiers
      const needsModifier = shortcut.meta || shortcut.ctrl

      if (needsModifier) {
        // Check platform-specific modifier
        if (this.platform === 'mac') {
          if (shortcut.meta && !e.metaKey) continue
        } else {
          if (shortcut.ctrl && !e.ctrlKey) continue
        }

        // Check shift modifier
        if (shortcut.shift !== undefined) {
          if (shortcut.shift && !e.shiftKey) continue
          if (!shortcut.shift && e.shiftKey) continue
        }

        // Check alt modifier
        if (shortcut.alt && !e.altKey) continue
      } else if (e.key !== 'Escape') {
        // For keys like Escape that don't need modifiers
        if (e.metaKey || e.ctrlKey) continue
      }

      // Prevent default if specified
      if (shortcut.preventDefault) {
        e.preventDefault()
      }

      // Stop propagation if specified
      if (shortcut.stopPropagation) {
        e.stopPropagation()
      }

      // Execute action
      if (typeof shortcut.action === 'function') {
        shortcut.action()
      } else {
        window.dispatchEvent(new CustomEvent(shortcut.action))
      }

      return true // Shortcut was handled
    }
    
    return false // No shortcut matched
  }
  
  getShortcuts(): ShortcutDefinition[] {
    return Array.from(this.shortcuts.values())
  }
  
  getShortcutDescription(id: string): string | undefined {
    return this.shortcuts.get(id)?.description
  }
  
  formatShortcut(shortcut: ShortcutDefinition): string {
    const parts: string[] = []
    
    if (this.platform === 'mac') {
      if (shortcut.meta !== false) parts.push('⌘')
      if (shortcut.shift) parts.push('⇧')
      if (shortcut.alt) parts.push('⌥')
      if (shortcut.ctrl) parts.push('⌃')
    } else {
      if (shortcut.ctrl !== false) parts.push('Ctrl')
      if (shortcut.shift) parts.push('Shift')
      if (shortcut.alt) parts.push('Alt')
      if (shortcut.meta) parts.push('Win')
    }
    
    // Format key
    const key = Array.isArray(shortcut.key) ? shortcut.key[0] : shortcut.key
    let displayKey = key
    
    // Special key formatting
    if (key === 'Escape') displayKey = 'ESC'
    else if (key === 'Enter' || key === 'Return') displayKey = this.platform === 'mac' ? '↩' : 'Enter'
    else if (key.startsWith('Key')) displayKey = key.substring(3)
    else if (key === ',') displayKey = ','
    
    parts.push(displayKey)
    
    return parts.join(this.platform === 'mac' ? '' : '+')
  }
}

// Export singleton instance
export const keyboardShortcutService = new KeyboardShortcutService()